import React, { useCallback, useEffect, useMemo, useState } from 'react'
import Button from '../common/Button'
import ConfirmDialog from '../common/ConfirmDialog'
import Icon from '../common/Icon'
import Spinner from '../common/Spinner'
import { useAuth } from '../../context/AuthContext'
import {
  RoleRef,
  UserWithRoles,
  assignRole,
  createUser,
  deleteUser,
  getRoles,
  getUsers,
  removeRole,
} from '../../api/usersService'

// Pre-checked in the Add-user form: the default role for new users.
const DEFAULT_NEW_USER_ROLE = 'GailsBasicUser'

// Render a camelCase role name so it only ever wraps on word boundaries (capital
// letters), never mid-word, and always breaks before a trailing "User".
// e.g. "GailsBasicUser" -> "GailsBasic" / "User", not "GailsBasicUse" / "r".
const renderRoleHeader = (name: string): React.ReactNode => {
  const words = name.match(
    /[A-Z]+(?=[A-Z][a-z])|[A-Z]?[a-z]+|[A-Z]+|[0-9]+/g
  ) || [name]
  const endsWithUser = words.length > 1 && words[words.length - 1] === 'User'
  const head = endsWithUser ? words.slice(0, -1) : words
  return (
    <>
      {head.map((w, i) => (
        <React.Fragment key={i}>
          {i > 0 && <wbr />}
          {w}
        </React.Fragment>
      ))}
      {endsWithUser && (
        <>
          <br />
          User
        </>
      )}
    </>
  )
}

const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/

// Pull the FastAPI error detail out of an axios error, falling back to a default.
const serverMessage = (e: unknown, fallback: string): string => {
  const detail = (e as { response?: { data?: { detail?: string } } })?.response
    ?.data?.detail
  return typeof detail === 'string' && detail ? detail : fallback
}

const InlineLoading: React.FC<{ text?: string }> = ({ text = 'Loading…' }) => (
  <div className="user-roles__loading">
    <Spinner text={text} />
  </div>
)

const UserRolesPanel: React.FC = () => {
  const { user: currentUser } = useAuth()
  const [roles, setRoles] = useState<RoleRef[]>([])
  const [users, setUsers] = useState<UserWithRoles[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [search, setSearch] = useState('')
  // "email:roleId" keys currently being toggled, to disable just that checkbox.
  const [pending, setPending] = useState<Set<string>>(new Set())
  // User awaiting delete confirmation, and the one being deleted.
  const [pendingDelete, setPendingDelete] = useState<UserWithRoles | null>(null)
  const [deleting, setDeleting] = useState(false)

  // Add-user form
  const [addOpen, setAddOpen] = useState(false)
  const [newEmail, setNewEmail] = useState('')
  const [newRoleIds, setNewRoleIds] = useState<Set<number>>(new Set())
  const [adding, setAdding] = useState(false)
  const [addError, setAddError] = useState<string | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const [r, u] = await Promise.all([getRoles(), getUsers()])
      setRoles(r)
      setUsers(u)
    } catch (e) {
      setError(serverMessage(e, 'Could not load users and roles.'))
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    load()
  }, [load])

  const toggleRole = async (
    user: UserWithRoles,
    role: RoleRef,
    hasRole: boolean
  ) => {
    const key = `${user.email}:${role.role_id}`
    setPending(prev => new Set(prev).add(key))
    setError(null)
    try {
      if (hasRole) {
        await removeRole(user.email, role.role_id)
      } else {
        await assignRole(user.email, role.role_id)
      }
      // Update local state without a full reload.
      setUsers(prev =>
        prev.map(u =>
          u.email === user.email
            ? {
                ...u,
                roles: hasRole
                  ? u.roles.filter(r => r.role_id !== role.role_id)
                  : [...u.roles, role],
              }
            : u
        )
      )
    } catch (e) {
      setError(
        serverMessage(
          e,
          `Could not ${hasRole ? 'remove' : 'assign'} ${role.name} for ${user.email}.`
        )
      )
    } finally {
      setPending(prev => {
        const next = new Set(prev)
        next.delete(key)
        return next
      })
    }
  }

  const doDelete = async () => {
    if (!pendingDelete) return
    setDeleting(true)
    setError(null)
    try {
      await deleteUser(pendingDelete.email)
      setUsers(prev => prev.filter(u => u.email !== pendingDelete.email))
      setPendingDelete(null)
    } catch (e) {
      setError(serverMessage(e, `Could not remove ${pendingDelete.email}.`))
    } finally {
      setDeleting(false)
    }
  }

  const openAddForm = () => {
    // Default new users to GailsBasicUser.
    setNewRoleIds(
      new Set(
        roles
          .filter(r => r.name === DEFAULT_NEW_USER_ROLE)
          .map(r => r.role_id)
      )
    )
    setAddOpen(true)
  }

  const resetAddForm = () => {
    setAddOpen(false)
    setNewEmail('')
    setNewRoleIds(new Set())
    setAddError(null)
  }

  const toggleNewRole = (roleId: number) => {
    setNewRoleIds(prev => {
      const next = new Set(prev)
      next.has(roleId) ? next.delete(roleId) : next.add(roleId)
      return next
    })
  }

  const submitAdd = async () => {
    const email = newEmail.trim().toLowerCase()
    if (!EMAIL_RE.test(email)) {
      setAddError('Enter a valid email address.')
      return
    }
    setAdding(true)
    setAddError(null)
    try {
      const created = await createUser(email, Array.from(newRoleIds))
      // Drop the new user straight into the list (sorted by email like the backend).
      setUsers(prev =>
        [...prev.filter(u => u.email.toLowerCase() !== email), created].sort(
          (a, b) => a.email.localeCompare(b.email)
        )
      )
      resetAddForm()
    } catch (e) {
      setAddError(serverMessage(e, 'Could not add the user.'))
    } finally {
      setAdding(false)
    }
  }

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    if (!q) return users
    return users.filter(u => u.email.toLowerCase().includes(q))
  }, [users, search])

  if (loading) {
    return <InlineLoading />
  }

  return (
    <div className="user-roles">
      <div className="user-roles__header">
        <div>
          <h3 className="user-roles__title">Users &amp; Roles</h3>
          <p className="user-roles__subtitle">
            Assign or remove BreadGPT roles. Changes take effect on the user’s
            next sign-in.
          </p>
        </div>
        {!addOpen && (
          <Button
            variant="primary"
            className="user-roles__add-btn"
            onClick={openAddForm}
          >
            <Icon name="new-chat" size={16} /> Add user
          </Button>
        )}
      </div>

      {error && <div className="user-roles__error">{error}</div>}

      {addOpen && (
        <div className="user-roles__add">
          <label className="user-roles__add-field">
            <span className="user-roles__label">Email</span>
            <span className="user-roles__hint">
              Must be the user’s exact Microsoft sign-in (UPN) — otherwise it
              won’t match when they log in.
            </span>
            <input
              className="user-roles__input"
              type="email"
              placeholder="someone@breadltd.co.uk"
              value={newEmail}
              autoFocus
              onChange={e => setNewEmail(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Enter' && !adding) submitAdd()
              }}
            />
          </label>

          <div className="user-roles__add-field">
            <span className="user-roles__label">Roles</span>
            <div className="user-roles__add-roles">
              {roles.map(role => (
                <label key={role.role_id} className="user-roles__add-role">
                  <input
                    type="checkbox"
                    className="user-roles__checkbox"
                    checked={newRoleIds.has(role.role_id)}
                    onChange={() => toggleNewRole(role.role_id)}
                  />
                  {role.name}
                </label>
              ))}
            </div>
          </div>

          {addError && <div className="user-roles__error">{addError}</div>}

          <div className="user-roles__add-actions">
            <Button variant="secondary" onClick={resetAddForm} disabled={adding}>
              Cancel
            </Button>
            <Button variant="primary" onClick={submitAdd} isLoading={adding}>
              Add user
            </Button>
          </div>
        </div>
      )}

      <input
        className="user-roles__search"
        placeholder="Search by email…"
        value={search}
        onChange={e => setSearch(e.target.value)}
      />

      <div className="user-roles__table">
        <div className="user-roles__row user-roles__row--head">
          <span className="user-roles__cell user-roles__cell--email">User</span>
          {roles.map(role => (
            <span
              key={role.role_id}
              className="user-roles__cell user-roles__cell--role"
              title={role.description || undefined}
            >
              {renderRoleHeader(role.name)}
            </span>
          ))}
          <span className="user-roles__cell user-roles__cell--actions" />
        </div>

        {filtered.length === 0 ? (
          <div className="user-roles__empty">No users match “{search}”.</div>
        ) : (
          filtered.map(user => (
            <div key={user.email} className="user-roles__row">
              <span className="user-roles__cell user-roles__cell--email">
                {user.email}
              </span>
              {roles.map(role => {
                const hasRole = user.roles.some(
                  r => r.role_id === role.role_id
                )
                const key = `${user.email}:${role.role_id}`
                const busy = pending.has(key)
                return (
                  <span
                    key={role.role_id}
                    className="user-roles__cell user-roles__cell--role"
                  >
                    <input
                      type="checkbox"
                      className="user-roles__checkbox"
                      checked={hasRole}
                      disabled={busy}
                      onChange={() => toggleRole(user, role, hasRole)}
                      aria-label={`${role.name} for ${user.email}`}
                    />
                  </span>
                )
              })}
              <span className="user-roles__cell user-roles__cell--actions">
                {user.email.toLowerCase() !==
                  (currentUser?.email || '').toLowerCase() && (
                  <button
                    className="user-roles__icon-btn"
                    onClick={() => setPendingDelete(user)}
                    aria-label={`Remove ${user.email}`}
                    title="Remove user"
                  >
                    <Icon name="trash" size={16} />
                  </button>
                )}
              </span>
            </div>
          ))
        )}
      </div>

      <ConfirmDialog
        isOpen={pendingDelete !== null}
        title="Remove user"
        message={`Remove ${pendingDelete?.email} from BreadGPT? They will lose access to the assistant until an admin adds them again.`}
        confirmLabel={deleting ? 'Removing…' : 'Remove'}
        onConfirm={doDelete}
        onCancel={() => setPendingDelete(null)}
      />
    </div>
  )
}

export default UserRolesPanel
